// Fixed rubric dimensions with max scores
export const DIMENSIONS = [
  { key: 'outcome_clarity', label: 'Outcome clarity + deliverable', max: 15 },
  { key: 'role_assignment', label: 'Role / perspective assignment', max: 6 },
  { key: 'context_background', label: 'Context + background', max: 12 },
  { key: 'constraints_scope', label: 'Constraints + scope boundaries', max: 15 },
  { key: 'inputs_sources', label: 'Inputs, sources, and data handling', max: 10 },
  { key: 'output_format', label: 'Output format + structure', max: 10 },
  { key: 'quality_bar', label: 'Quality bar + success criteria', max: 10 },
  { key: 'method_process', label: 'Method / process instructions', max: 8 },
  { key: 'risk_controls', label: 'Risk controls', max: 7 },
  { key: 'iteration_hygiene', label: 'Iteration + session hygiene', max: 7 },
] as const;

export type DimensionKey = typeof DIMENSIONS[number]['key'];

// Fallback templates for "To increase your score"
export const IMPROVEMENT_TEMPLATES: Record<DimensionKey, string> = {
  outcome_clarity: 'Specify the exact deliverable + format (e.g., 5 bullets, 200 words, table).',
  role_assignment: 'Assign a specific expert role relevant to the task.',
  context_background: "Add 2-3 key facts the AI can't infer (audience, background, goal).",
  constraints_scope: "Add explicit do/don't guardrails and scope boundaries.",
  inputs_sources: 'Provide the data/source to use and instructions for missing info.',
  output_format: 'Request a structured layout (sections, headings, order).',
  quality_bar: "Define what 'good' means (checklist, examples, measurable criteria).",
  method_process: 'Tell the AI the approach (steps, framework, order of operations).',
  risk_controls: 'Ask it to label assumptions/uncertainty and not invent facts.',
  iteration_hygiene: 'Ask for a first draft, then questions, then a revised final version.',
};

// Keywords for matching dimensions
const DIMENSION_KEYWORDS: Record<DimensionKey, string[]> = {
  outcome_clarity: ['outcome', 'deliverable', 'clarity', 'goal', 'objective', 'result', 'output'],
  role_assignment: ['role', 'perspective', 'persona', 'expert', 'act as'],
  context_background: ['context', 'background', 'audience', 'situation'],
  constraints_scope: ['constraint', 'scope', 'boundary', 'limit', 'don\'t', 'avoid', 'exclude'],
  inputs_sources: ['input', 'source', 'data', 'reference', 'document', 'material'],
  output_format: ['format', 'structure', 'layout', 'organize', 'section', 'heading'],
  quality_bar: ['quality', 'success', 'criteria', 'measure', 'standard', 'checklist'],
  method_process: ['method', 'process', 'step', 'approach', 'framework', 'how to'],
  risk_controls: ['risk', 'assumption', 'uncertainty', 'hedge', 'caveat', 'hallucination'],
  iteration_hygiene: ['iteration', 'refine', 'draft', 'revise', 'session', 'follow-up'],
};

export interface ParsedDimension {
  key: DimensionKey;
  label: string;
  max: number;
  score: number;
  rationale: string;
  whatWorked: string;
  improvement: string;
}

export interface ChatcraftScores {
  convergence: number; // 0-12
  constraintMastery: number; // 0-10
  sessionHygiene: number; // 0-8
  total: number; // 0-30
}

export interface ParsedRubricData {
  overallScore: number;
  dimensions: ParsedDimension[];
  chatcraft?: ChatcraftScores;
  topUpgrades: string[];
}

// Parse dimension scores from evaluation markdown
export function parseDimensionScores(markdown: string): Map<string, { score: number; rationale: string }> {
  const result = new Map<string, { score: number; rationale: string }>();
  
  if (!markdown) return result;

  const lines = markdown.split('\n');
  let currentDimension: string | null = null;
  let currentRationale = '';
  
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim();
    
    // Look for dimension headers with scores like "1. Outcome clarity + deliverable: 11/15"
    // or "Outcome clarity: 11/15" or "| Outcome clarity | 11 | ..."
    const scoreMatch = line.match(/(?:\d+\.\s*)?([^|:]+?)(?::\s*|\s*\|\s*)(\d+)(?:\s*\/\s*(\d+))?/i);
    
    if (scoreMatch) {
      const dimName = scoreMatch[1].trim().toLowerCase();
      const score = parseInt(scoreMatch[2], 10);
      
      // Find matching dimension
      for (const dim of DIMENSIONS) {
        const dimLabel = dim.label.toLowerCase();
        const keywords = DIMENSION_KEYWORDS[dim.key];
        
        if (dimLabel.includes(dimName) || dimName.includes(dimLabel.split(' ')[0]) ||
            keywords.some(kw => dimName.includes(kw))) {
          
          // Get rationale from next line(s)
          let rationale = '';
          for (let j = i + 1; j < Math.min(i + 4, lines.length); j++) {
            const nextLine = lines[j].trim();
            if (nextLine && !nextLine.match(/^\d+\.\s/) && !nextLine.match(/^\|/)) {
              rationale = nextLine.replace(/^[-•*]\s*/, '');
              break;
            }
          }
          
          result.set(dim.key, { score, rationale });
          break;
        }
      }
    }
  }
  
  return result;
}

// Parse top upgrades/improvements from markdown
export function parseTopUpgrades(markdown: string): string[] {
  const upgrades: string[] = [];
  if (!markdown) return upgrades;

  const lines = markdown.split('\n');
  let inUpgradeSection = false;
  
  for (const line of lines) {
    const lowerLine = line.toLowerCase();
    
    // Detect upgrade section headers
    if (lowerLine.includes('top') && (lowerLine.includes('upgrade') || lowerLine.includes('action') || lowerLine.includes('improvement'))) {
      inUpgradeSection = true;
      continue;
    }
    
    if (lowerLine.includes('next step') || lowerLine.includes('quick win')) {
      inUpgradeSection = true;
      continue;
    }
    
    // Collect items while in upgrade section
    if (inUpgradeSection && upgrades.length < 3) {
      const itemMatch = line.match(/^[\s]*(?:\d+[.)]\s*|\*\s*|-\s*|•\s*)(.+)/);
      if (itemMatch && itemMatch[1].trim().length > 10) {
        upgrades.push(itemMatch[1].trim());
      }
    }
    
    // Stop after collecting 3 or hitting a new section
    if (upgrades.length >= 3 || (inUpgradeSection && line.match(/^#+\s/))) {
      break;
    }
  }
  
  return upgrades;
}

// Match an upgrade string to the most relevant dimension
export function matchUpgradeToDimension(upgrade: string): DimensionKey | null {
  const lowerUpgrade = upgrade.toLowerCase();
  let bestMatch: DimensionKey | null = null;
  let bestScore = 0;
  
  for (const dim of DIMENSIONS) {
    const keywords = DIMENSION_KEYWORDS[dim.key];
    let matchScore = 0;
    
    for (const kw of keywords) {
      if (lowerUpgrade.includes(kw)) {
        matchScore += kw.length;
      }
    }
    
    if (matchScore > bestScore) {
      bestScore = matchScore;
      bestMatch = dim.key;
    }
  }
  
  return bestScore >= 4 ? bestMatch : null;
}

// Get improvement text for a dimension
export function getImprovementText(
  dimensionKey: DimensionKey,
  score: number,
  max: number,
  matchedUpgrade?: string
): string {
  const percentage = (score / max) * 100;
  
  // If >= 90%, congratulate
  if (percentage >= 90) {
    return 'Keep doing this.';
  }
  
  // Use matched upgrade if available
  if (matchedUpgrade) {
    return matchedUpgrade;
  }
  
  // Use template based on score band
  const template = IMPROVEMENT_TEMPLATES[dimensionKey];
  
  if (percentage < 50) {
    return template; // Full improvement needed
  } else {
    // 50-89%: soften the language
    return `Tighten: ${template.charAt(0).toLowerCase()}${template.slice(1)}`;
  }
}

// Extract "what worked" from rationale
export function extractWhatWorked(rationale: string, dimensionKey: DimensionKey): string {
  if (!rationale || rationale.length < 10) {
    // Fallback based on dimension
    const fallbacks: Record<DimensionKey, string> = {
      outcome_clarity: 'Some goal indication present.',
      role_assignment: 'No explicit role assigned.',
      context_background: 'Minimal context provided.',
      constraints_scope: 'Basic scope implied.',
      inputs_sources: 'Input handling not specified.',
      output_format: 'Format not explicitly defined.',
      quality_bar: 'Quality criteria not defined.',
      method_process: 'Method not specified.',
      risk_controls: 'Risk controls not mentioned.',
      iteration_hygiene: 'Iteration plan not included.',
    };
    return fallbacks[dimensionKey];
  }
  
  // Clean up rationale to make it a single sentence
  let cleaned = rationale.replace(/^[-•*]\s*/, '').trim();
  if (!cleaned.endsWith('.')) cleaned += '.';
  
  // Truncate if too long
  if (cleaned.length > 100) {
    cleaned = cleaned.substring(0, 97) + '...';
  }
  
  return cleaned;
}

// Map snake_case keys from DB to camelCase for UI
export function mapChatcraftFromDb(dbScores?: Record<string, number>): ChatcraftScores | undefined {
  if (!dbScores) return undefined;
  
  const convergence = dbScores.convergence_efficiency ?? dbScores.convergence ?? 0;
  const constraintMastery = dbScores.constraint_mastery ?? dbScores.constraintMastery ?? 0;
  const sessionHygiene = dbScores.session_hygiene ?? dbScores.sessionHygiene ?? 0;
  
  const total = convergence + constraintMastery + sessionHygiene;
  
  // Only return if we have actual scores
  if (total === 0 && convergence === 0 && constraintMastery === 0 && sessionHygiene === 0) {
    return undefined;
  }
  
  return { convergence, constraintMastery, sessionHygiene, total };
}

// Parse Chatcraft scores for chat submissions
export function parseChatcraftScores(markdown: string): ChatcraftScores | undefined {
  if (!markdown) return undefined;
  
  const lowerMarkdown = markdown.toLowerCase();
  
  // Remove strict "chatcraft" keyword requirement
  // Instead, look for chat-specific score patterns
  const hasConvergence = /convergence/i.test(markdown);
  const hasConstraintMastery = /constraint.*mastery/i.test(markdown);
  const hasSessionHygiene = /session.*hygiene|drift/i.test(markdown);
  
  if (!hasConvergence && !hasConstraintMastery && !hasSessionHygiene) {
    return undefined;
  }
  
  const scores: ChatcraftScores = {
    convergence: 0,
    constraintMastery: 0,
    sessionHygiene: 0,
    total: 0,
  };
  
  // Look for patterns like "Convergence: 8/12" or "convergence efficiency: 8"
  const convergenceMatch = markdown.match(/convergence[^:]*:\s*(\d+)/i);
  if (convergenceMatch) {
    scores.convergence = Math.min(12, parseInt(convergenceMatch[1], 10));
  }
  
  const constraintMatch = markdown.match(/constraint[^:]*:\s*(\d+)/i);
  if (constraintMatch) {
    scores.constraintMastery = Math.min(10, parseInt(constraintMatch[1], 10));
  }
  
  const hygieneMatch = markdown.match(/(?:session|hygiene|drift)[^:]*:\s*(\d+)/i);
  if (hygieneMatch) {
    scores.sessionHygiene = Math.min(8, parseInt(hygieneMatch[1], 10));
  }
  
  scores.total = scores.convergence + scores.constraintMastery + scores.sessionHygiene;
  
  return scores.total > 0 ? scores : undefined;
}

// Dimension scores can be either a number or an object with score/rationale
type DimensionScoreValue = number | { score: number; rationale?: string };

// Main parsing function that combines everything
export function parseRubricData(
  evaluationMarkdown: string,
  overallScore: number,
  dimensionScoresJson?: Record<string, DimensionScoreValue>,
  chatcraftScoresJson?: Record<string, number>
): ParsedRubricData {
  // Parse dimensions from markdown or use provided JSON
  const parsedFromMarkdown = parseDimensionScores(evaluationMarkdown);
  const topUpgrades = parseTopUpgrades(evaluationMarkdown);
  
  // Create map from JSON if provided - normalize to { score, rationale } format
  const dimensionMap = new Map<string, { score: number; rationale: string }>();
  if (dimensionScoresJson) {
    Object.entries(dimensionScoresJson).forEach(([key, value]) => {
      if (typeof value === 'number') {
        dimensionMap.set(key, { score: value, rationale: '' });
      } else {
        dimensionMap.set(key, { score: value.score, rationale: value.rationale ?? '' });
      }
    });
  } else {
    parsedFromMarkdown.forEach((value, key) => {
      dimensionMap.set(key, value);
    });
  }
  
  // Match upgrades to dimensions
  const upgradeAssignments = new Map<DimensionKey, string>();
  for (const upgrade of topUpgrades) {
    const matchedDim = matchUpgradeToDimension(upgrade);
    if (matchedDim && !upgradeAssignments.has(matchedDim)) {
      upgradeAssignments.set(matchedDim, upgrade);
    }
  }
  
  // Build parsed dimensions - ALWAYS render all 10, even with score = 0
  const dimensions: ParsedDimension[] = DIMENSIONS.map(dim => {
    const parsed = dimensionMap.get(dim.key);
    // Explicit 0 fallback - never filter out dimensions
    const score = parsed?.score !== undefined && parsed?.score !== null ? parsed.score : 0;
    const rationale = parsed?.rationale ?? '';
    
    return {
      key: dim.key,
      label: dim.label,
      max: dim.max,
      score,
      rationale,
      whatWorked: extractWhatWorked(rationale, dim.key),
      improvement: getImprovementText(dim.key, score, dim.max, upgradeAssignments.get(dim.key)),
    };
  });
  
  // Parse Chatcraft for chats - prefer DB JSON, fallback to markdown
  const chatcraftFromDb = mapChatcraftFromDb(chatcraftScoresJson);
  const chatcraftFromMarkdown = parseChatcraftScores(evaluationMarkdown);
  const chatcraft = chatcraftFromDb || chatcraftFromMarkdown;
  
  return {
    overallScore,
    dimensions,
    chatcraft,
    topUpgrades,
  };
}

// Calculate overall score from dimensions if not provided
export function calculateOverallFromDimensions(dimensions: ParsedDimension[]): number {
  const totalScore = dimensions.reduce((sum, d) => sum + d.score, 0);
  const totalMax = dimensions.reduce((sum, d) => sum + d.max, 0);
  return Math.round((totalScore / totalMax) * 100);
}
